<script lang="ts" setup>
import { computed, reactive, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ValidationResult } from '@/types'

const props = defineProps<{
  domains: ValidationResult[]
  loading: boolean
  selectedDomains: string[]
}>()

const emit = defineEmits<{
  'selection-change': [domains: string[]]
  import: []
}>()

const { t } = useI18n()

const data = reactive({
  searchQuery: '',
  showOnlyImportable: true
})

// Filter domains based on search and importable filter
const filteredDomains = computed(() => {
  let result = [...props.domains]

  // Filter by importable status
  if (data.showOnlyImportable) {
    result = result.filter((d) => d.can_import && !d.exists_in_db)
  }

  // Filter by search query
  if (data.searchQuery.trim()) {
    const query = data.searchQuery.toLowerCase().trim()
    result = result.filter((d) => d.domain.toLowerCase().includes(query))
  }

  return result
})

// Check if all visible domains are selected
const allSelected = computed(() => {
  const importable = filteredDomains.value.filter((d) => d.can_import && !d.exists_in_db)
  if (importable.length === 0) return false
  return importable.every((d) => props.selectedDomains.includes(d.domain))
})

// Check if some but not all are selected
const someSelected = computed(() => {
  const importable = filteredDomains.value.filter((d) => d.can_import && !d.exists_in_db)
  if (importable.length === 0) return false
  const selectedCount = importable.filter((d) => props.selectedDomains.includes(d.domain)).length
  return selectedCount > 0 && selectedCount < importable.length
})

// Toggle selection for a single domain
const toggleDomain = (domain: string) => {
  const newSelection = props.selectedDomains.includes(domain)
    ? props.selectedDomains.filter((d) => d !== domain)
    : [...props.selectedDomains, domain]
  emit('selection-change', newSelection)
}

// Toggle all visible importable domains
const toggleAll = () => {
  const importable = filteredDomains.value
    .filter((d) => d.can_import && !d.exists_in_db)
    .map((d) => d.domain)

  if (allSelected.value) {
    // Deselect all visible
    const newSelection = props.selectedDomains.filter((d) => !importable.includes(d))
    emit('selection-change', newSelection)
  } else {
    // Select all visible
    const newSelection = [...new Set([...props.selectedDomains, ...importable])]
    emit('selection-change', newSelection)
  }
}

// Format date for display
const formatDate = (dateString?: string) => {
  if (!dateString) return '-'
  try {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  } catch {
    return dateString
  }
}

// Get status class for a domain
const getStatusClass = (domain: ValidationResult) => {
  if (domain.exists_in_db) return 'status-imported'
  if (domain.can_import) return 'status-available'
  return 'status-unavailable'
}

// Get status label for a domain
const getStatusLabel = (domain: ValidationResult) => {
  if (domain.exists_in_db) return t('dashboard.admin.migration.status.imported')
  if (domain.can_import) return t('dashboard.admin.migration.status.available')
  return t('dashboard.admin.migration.status.cannotImport')
}

// Clear search when filter changes
watch(() => data.showOnlyImportable, () => {
  // Optionally clear selection when filter changes
})
</script>

<template>
  <div class="domain-list-container">
    <!-- Table Header with Search -->
    <div class="table-header">
      <div class="header-left">
        <h2 class="table-title">{{ $t('dashboard.admin.migration.table.domain') }}</h2>
        <span class="domain-count">{{ filteredDomains.length }} {{ $t('dashboard.domains.overview.domains') }}</span>
      </div>

      <div class="header-right">
        <!-- Filter Toggle -->
        <label class="filter-toggle">
          <input
            v-model="data.showOnlyImportable"
            type="checkbox"
            class="toggle-input"
          />
          <span class="toggle-label">{{ $t('dashboard.admin.migration.status.available') }}</span>
        </label>

        <!-- Search -->
        <div class="search-box">
          <v-icon icon="mdi-magnify" size="18" class="search-icon" />
          <input
            v-model="data.searchQuery"
            type="text"
            class="search-input"
            :placeholder="$t('dashboard.admin.migration.table.search')"
          />
          <button
            v-if="data.searchQuery"
            class="clear-search"
            @click="data.searchQuery = ''"
          >
            <v-icon icon="mdi-close" size="16" />
          </button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <v-progress-circular indeterminate color="primary" size="40" />
      <span>{{ $t('dashboard.admin.migration.loading') }}</span>
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredDomains.length === 0" class="empty-state">
      <v-icon icon="mdi-domain" size="48" class="empty-icon" />
      <h3>{{ $t('dashboard.admin.migration.empty.title') }}</h3>
      <p>{{ $t('dashboard.admin.migration.empty.subtitle') }}</p>
    </div>

    <!-- Domain Table -->
    <div v-else class="glass-table">
      <!-- Table Head -->
      <div class="table-head">
        <div class="head-cell cell-checkbox">
          <label class="checkbox-wrapper">
            <input
              type="checkbox"
              :checked="allSelected"
              :indeterminate="someSelected"
              @change="toggleAll"
            />
            <span class="checkmark"></span>
          </label>
        </div>
        <div class="head-cell cell-domain">{{ $t('dashboard.admin.migration.table.domain') }}</div>
        <div class="head-cell cell-expiry">{{ $t('dashboard.admin.migration.table.expiryDate') }}</div>
        <div class="head-cell cell-status">{{ $t('dashboard.admin.migration.table.status') }}</div>
      </div>

      <!-- Table Body (Scrollable) -->
      <div class="table-body">
        <div
          v-for="domain in filteredDomains"
          :key="domain.domain"
          class="table-row"
          :class="{
            'row-selected': selectedDomains.includes(domain.domain),
            'row-disabled': !domain.can_import || domain.exists_in_db
          }"
          @click="domain.can_import && !domain.exists_in_db && toggleDomain(domain.domain)"
        >
          <div class="row-cell cell-checkbox">
            <label class="checkbox-wrapper" @click.stop>
              <input
                type="checkbox"
                :checked="selectedDomains.includes(domain.domain)"
                :disabled="!domain.can_import || domain.exists_in_db"
                @change="toggleDomain(domain.domain)"
              />
              <span class="checkmark"></span>
            </label>
          </div>
          <div class="row-cell cell-domain">
            <span class="domain-name">{{ domain.domain }}</span>
          </div>
          <div class="row-cell cell-expiry">
            <span class="expiry-date">{{ formatDate(domain.inwx_expiry_date) }}</span>
          </div>
          <div class="row-cell cell-status">
            <span class="status-badge" :class="getStatusClass(domain)">
              {{ getStatusLabel(domain) }}
            </span>
            <span v-if="domain.reason && !domain.can_import" class="status-reason">
              {{ domain.reason }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Selection Summary -->
    <div v-if="selectedDomains.length > 0" class="selection-summary">
      <span class="selection-count">
        {{ $t('dashboard.admin.migration.table.selectedCount', { count: selectedDomains.length }) }}
      </span>
      <button class="clear-selection" @click="emit('selection-change', [])">
        {{ $t('dashboard.admin.migration.table.deselectAll') }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.domain-list-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-height: 0;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.table-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.domain-count {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.filter-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  .toggle-input {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: #26a69a;
  }

  .toggle-label {
    font-size: 0.85rem;
    color: rgba(200, 218, 255, 0.7);
  }
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  color: rgba(200, 218, 255, 0.4);
  pointer-events: none;
}

.search-input {
  width: 220px;
  padding: 8px 36px 8px 38px;
  background: rgba(15, 23, 48, 0.6);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.85rem;
  outline: none;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(200, 218, 255, 0.4);
  }

  &:focus {
    border-color: rgba(120, 163, 255, 0.3);
    background: rgba(15, 23, 48, 0.8);
  }
}

.clear-search {
  position: absolute;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: transparent;
  border: none;
  color: rgba(200, 218, 255, 0.5);
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
  }
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 48px;
  color: rgba(200, 218, 255, 0.6);
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px;
  text-align: center;

  .empty-icon {
    color: rgba(200, 218, 255, 0.3);
  }

  h3 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
  }

  p {
    margin: 0;
    font-size: 0.9rem;
    color: rgba(200, 218, 255, 0.5);
  }
}

/* Glass Table */
.glass-table {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: rgba(15, 23, 48, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
}

.table-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(120, 163, 255, 0.3);
    border-radius: 3px;

    &:hover {
      background: rgba(120, 163, 255, 0.5);
    }
  }
}

.table-head {
  display: grid;
  grid-template-columns: 48px 1fr 140px 180px;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
}

.head-cell {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(200, 218, 255, 0.5);
}

.table-row {
  display: grid;
  grid-template-columns: 48px 1fr 140px 180px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  cursor: pointer;
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover:not(.row-disabled) {
    background: rgba(120, 163, 255, 0.05);
  }

  &.row-selected {
    background: rgba(0, 150, 136, 0.1);

    &:hover {
      background: rgba(0, 150, 136, 0.15);
    }
  }

  &.row-disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.row-cell {
  display: flex;
  align-items: center;
}

.cell-checkbox {
  justify-content: center;
}

/* Checkbox Styling */
.checkbox-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  cursor: pointer;

  input[type="checkbox"] {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    width: 100%;
    height: 100%;

    &:disabled {
      cursor: not-allowed;
    }
  }

  .checkmark {
    position: relative;
    width: 18px;
    height: 18px;
    background: rgba(15, 23, 48, 0.8);
    border: 1.5px solid rgba(120, 163, 255, 0.3);
    border-radius: 4px;
    transition: all 0.15s ease;
  }

  input:checked + .checkmark {
    background: #26a69a;
    border-color: #26a69a;

    &::after {
      content: '';
      position: absolute;
      left: 50%;
      top: 45%;
      width: 5px;
      height: 10px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: translate(-50%, -50%) rotate(45deg);
    }
  }

  input:indeterminate + .checkmark {
    background: #26a69a;
    border-color: #26a69a;

    &::after {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      width: 10px;
      height: 2px;
      background: white;
      transform: translate(-50%, -50%);
    }
  }

  input:disabled + .checkmark {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

/* Domain Name */
.domain-name {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

/* Expiry Date */
.expiry-date {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.7);
}

/* Status Badge */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;

  &.status-available {
    background: rgba(76, 175, 80, 0.15);
    color: #81c784;
  }

  &.status-imported {
    background: rgba(0, 150, 136, 0.15);
    color: #26a69a;
  }

  &.status-unavailable {
    background: rgba(244, 67, 54, 0.15);
    color: #e57373;
  }
}

.status-reason {
  margin-left: 8px;
  font-size: 0.75rem;
  color: rgba(200, 218, 255, 0.4);
}

/* Selection Summary */
.selection-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: rgba(0, 150, 136, 0.1);
  border: 1px solid rgba(0, 150, 136, 0.2);
  border-radius: 8px;
}

.selection-count {
  font-size: 0.9rem;
  font-weight: 500;
  color: #26a69a;
}

.clear-selection {
  padding: 6px 12px;
  background: transparent;
  border: 1px solid rgba(0, 150, 136, 0.3);
  border-radius: 6px;
  color: #26a69a;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(0, 150, 136, 0.1);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .table-header {
    flex-direction: column;
    align-items: stretch;
  }

  .header-right {
    flex-direction: column;
    align-items: stretch;
  }

  .search-input {
    width: 100%;
  }

  .table-head,
  .table-row {
    grid-template-columns: 40px 1fr 100px;
  }

  .cell-status {
    display: none;
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .table-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .glass-table {
    background: rgba(255, 255, 255, 0.8);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .domain-name {
    color: rgba(15, 23, 42, 0.9);
  }

  .search-input {
    background: rgba(255, 255, 255, 0.9);
    color: rgba(15, 23, 42, 0.9);
  }
}
</style>
